import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, BookOpen, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const sections = [
  {
    title: "1. Criar Conta e Entrar",
    content: `**Primeiro acesso:** Clique em "Registar", preencha o nome, email e palavra-passe (min. 6 caracteres), e clique "Criar conta".

**Acessos seguintes:** No separador "Entrar", insira o email e a palavra-passe e clique "Entrar".

Cada professor tem a sua conta individual. Os testes de um professor não são visíveis para outros.`
  },
  {
    title: "2. Criar um Novo Teste",
    content: `No Painel, clique em **"+ Novo Teste"** e preencha:

- **Título do Teste** (ex: "Ficha de Avaliação - Matemática 3.º ano")
- **Disciplina** (ex: "Matemática")
- **Máximo de páginas por teste** - quantas fotos pode ter o teste de cada aluno (ex: 4). Pode ser menos na prática.`
  },
  {
    title: "3. Carregar a Corrigenda (Automático)",
    content: `Clique em **"Carregar Corrigenda (PDF)"** e selecione o PDF com as respostas corretas.

A IA analisa o documento (15-30 segundos) e extrai automaticamente as questões, respostas e sugere cotações.

**Revise sempre** as questões extraídas e ajuste as cotações conforme necessário.

Pode também adicionar questões manualmente na secção "Questões".`
  },
  {
    title: "4. Carregar os Testes dos Alunos",
    content: `**Testes escritos à mão:** Os alunos resolvem os testes normalmente, à mão, como sempre. Basta depois **digitalizar (scanner) ou fotografar com o telemóvel** - a IA lê a caligrafia dos alunos e corrige na mesma. Não é preciso que os alunos usem nada digital.

Arraste ou selecione as fotos/PDFs dos testes.

**Testes com várias páginas:**
- Carregue as fotos pela ordem: todas as páginas do aluno 1, depois do aluno 2, etc.
- O sistema agrupa automaticamente (ex: de 4 em 4)
- Se um aluno tiver menos páginas, clique **"Finalizar Aluno / Próximo Aluno"**

**Limites:** Até 30 alunos, formatos JPG, PNG ou PDF.`
  },
  {
    title: "5. Correção Automática com IA",
    content: `Clique em **"Corrigir Todos com IA"**. Para cada teste, a IA:

- Analisa todas as páginas do aluno
- Lê as respostas escritas
- Compara com a corrigenda
- Atribui pontuação e gera feedback em português

Cada teste demora 10-30 segundos. Quando terminar, clique "Rever Correções".`
  },
  {
    title: "6. Rever e Editar Correções",
    content: `Os alunos aparecem como cartões identificados por número (anonimização).

**Para rever:** Clique no cartão do aluno. Aparece o teste à esquerda e a correção à direita.

**Para editar:** Altere a pontuação ou feedback de qualquer questão e clique "Guardar Alterações".

**Para recorrigir:** Clique "Recorrigir com IA" para uma nova análise.

**Navegação de páginas:** Use os botões 1, 2, 3, 4 na parte inferior da imagem.`
  },
  {
    title: "7. Mapa de Avaliação e PDF",
    content: `O mapa mostra estatísticas (média, melhor nota, taxa de aprovação), a grelha de classificação completa e análise por questão.

**Logo:** Clique "Adicionar Logo" para carregar o logotipo do agrupamento.

**Exportar:** Clique "Exportar PDF" para descarregar o mapa.

**Menções:** Muito Bom (90-100%), Bom (70-89%), Suficiente (50-69%), Insuficiente (20-49%), Muito Insuficiente (0-19%).`
  },
  {
    title: "Perguntas Frequentes",
    content: `**Os testes podem ser escritos à mão?** Sim! Os alunos escrevem as respostas à mão, como sempre. Depois é só digitalizar ou fotografar o teste - a IA lê a caligrafia e corrige. É esta a forma normal de usar a plataforma.

**O teste pode ter menos páginas que o máximo?** Sim. Clique "Finalizar Aluno" quando acabar.

**É obrigatório ter 30 alunos?** Não. Funciona com 1 a 30 alunos.

**A IA erra?** Pode errar. Revise sempre todas as correções.

**Posso recorrigir?** Sim. Abra o teste e clique "Recorrigir com IA".

**Formatos aceites:** JPG, PNG e PDF.`
  }
];

export default function ManualPage() {
  const [openSection, setOpenSection] = useState(null);

  const toggle = (i) => setOpenSection(openSection === i ? null : i);

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fade-in" data-testid="manual-page">
      <div className="flex items-center gap-3 mb-2">
        <Link to="/dashboard">
          <Button variant="ghost" size="sm"><ArrowLeft className="h-4 w-4 mr-1" /> Voltar</Button>
        </Link>
      </div>

      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-primary/10">
          <BookOpen className="h-8 w-8 text-primary" />
        </div>
        <div>
          <h1 className="font-heading font-bold text-3xl tracking-tight">Manual do Utilizador</h1>
          <p className="text-muted-foreground text-sm">EduGrade - Correção Inteligente de Testes</p>
        </div>
      </div>

      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="pt-6">
          <p className="text-sm leading-relaxed">
            <strong>Fluxo resumido:</strong> Criar Teste &rarr; Carregar Corrigenda &rarr; Carregar Testes dos Alunos &rarr; Corrigir com IA &rarr; Rever Correções &rarr; Exportar Mapa de Avaliação (PDF)
          </p>
        </CardContent>
      </Card>

      <div className="space-y-3">
        {sections.map((section, i) => (
          <Card key={i} className="overflow-hidden">
            <button
              onClick={() => toggle(i)}
              className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors text-left"
              data-testid={`manual-section-${i}`}
            >
              <span className="font-heading font-semibold text-sm">{section.title}</span>
              {openSection === i
                ? <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                : <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
              }
            </button>
            {openSection === i && (
              <CardContent className="pt-0 pb-4 border-t">
                <div className="text-sm leading-relaxed text-muted-foreground whitespace-pre-line mt-3">
                  {section.content.split("**").map((part, j) =>
                    j % 2 === 1
                      ? <strong key={j} className="text-foreground">{part}</strong>
                      : <span key={j}>{part}</span>
                  )}
                </div>
              </CardContent>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
